import Modal from '@/components/Modal/Modal'
import type { RootState } from '@/store/rootReducer';
import FormControl from "@/components/FormControl/FormControl";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store/hooks';
import { getLeadList, getReferralUsers } from '@/store/slices/ReferralProgramSlice';
import type { ReferralUserItem } from '@/types/ReferralProgramtype';

export interface EditReferralProps {
    viewModalOpen: boolean;
    closeModal: () => void;
    onSave: (payload: any, isEditMode: boolean) => void;
    loading? : boolean | undefined
}

const EditReferralBusiness: React.FC<EditReferralProps> = ({ viewModalOpen, closeModal, onSave , loading = false}: EditReferralProps) => {
    const {
        control,
        reset,
        watch,
        setValue,
        handleSubmit
    } = useForm({
        mode: "onChange",
    });

    const dispatch = useAppDispatch();
    const { referralUsers, leads } = useSelector((state: RootState) => state.referrals);
    const { modalOpen } = useSelector((state: RootState) => state.global);

    const isEditMode = modalOpen?.data ? true : false;

    const selectedReferralUser = watch("referralUserId");
    const transactionalValue = watch("transactionValue");
    const transactionPercentage = watch("referralPercentage");

    const handleSave = handleSubmit(async (data: any) => {
        onSave(data, isEditMode);
    });

    const referralRewardValue = useMemo(() => {
        const value = parseFloat(transactionalValue);
        const percentage = parseFloat(transactionPercentage);
        if (!isNaN(value) && !isNaN(percentage) && value > 0 && percentage > 0) {
            return parseFloat(((value * percentage) / 100).toFixed(2));
        }
        return null;
    }, [transactionalValue, transactionPercentage]);

    useEffect(() => {
        setValue("referralReward", referralRewardValue);
    }, [referralRewardValue, setValue]);


    useEffect(() => {
        reset({
            referralUserId: referralUsers?.find((user: ReferralUserItem) => user.referralUserId === modalOpen?.data?.referralUserId)?.referralUserId || "",
            leadId: leads?.find((lead: any) => lead.leadId === modalOpen?.data?.leadId)?.leadId || 0,
            transactionValue: modalOpen?.data?.transactionValue,
            referralPercentage: modalOpen?.data?.referralPercentage,
            remark: modalOpen?.data?.remark || "",
            referralReward: modalOpen?.data?.referralReward
        });
    }, [viewModalOpen]);

    useEffect(() => {
        if (selectedReferralUser) {
            setValue("leadId", "");
            dispatch(getLeadList({
                From: 1,
                To: -1,
                ReferBy: selectedReferralUser,
                Text: ""
            }));

            if (!isEditMode || selectedReferralUser !== modalOpen?.data?.referralUserId) {
                const user = referralUsers?.find((u: ReferralUserItem) => u.referralUserId === selectedReferralUser);
                if (user && user.referralPercentage !== undefined) {
                    setValue("referralPercentage", user.referralPercentage);
                }
            }
        }
    }, [selectedReferralUser, referralUsers, isEditMode, setValue, dispatch]);


    useEffect(() => {
        if (viewModalOpen) {
            dispatch(getReferralUsers());
        }
    }, [viewModalOpen]);

    return (
        <div>
            <Modal
                modalOpen={viewModalOpen}
                closeModal={closeModal}
                heading={`Business`}
                width="600px"
                hideFooter={false}
                saveButtonText={isEditMode ? "Update" : "Add"}
                onSave={handleSave}
                isLoading={loading}
            >
                <form>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                        <FormControl
                            name="referralUserId"
                            control={control}
                            controlType="select"
                            enableSearch={false}
                            options={referralUsers ?? []}
                            label="Referral Users"
                            nameKey="userName"
                            idKey="referralUserId"
                            required
                            rules={{
                                validate: (value: any) =>
                                    value !== null && value !== undefined || "Refferral user is required"
                            }}
                        />

                        <FormControl
                            name="leadId"
                            control={control}
                            controlType="select"
                            enableSearch={false}
                            options={leads}
                            label="Companies"
                            nameKey="LeadName"
                            idKey="UserId"
                            required
                            isDisabled={!selectedReferralUser}
                            rules={{
                                validate: (value: any) =>
                                    value !== null && value !== undefined || "Company is required"
                            }}
                        />

                        <FormControl
                            name='transactionValue'
                            control={control}
                            controlType='input'
                            label="Transactional Value"
                            numbersOnly={true}
                            idKey=""
                            rules={{ required: "Transactional Value is required"}}
                            required
                        />

                        <FormControl
                            name='referralPercentage'
                            control={control}
                            controlType='input'
                            label="Transaction Percentage (%)"
                            numbersOnly={true}
                            inputProps={{
                                maxLength: 2
                            }}
                        />

                        {referralRewardValue !== null && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "10px 14px",
                                borderRadius: "8px",
                                backgroundColor: "#f0f7ff",
                                border: "1px solid #c3dafe",
                            }}>
                                <span style={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}>
                                    Referral Reward
                                </span>
                                <span style={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    color: "#1d4ed8",
                                }}>
                                    ₹ {referralRewardValue}
                                </span>
                            </div>
                        )}

                        <FormControl
                            name='remark'
                            control={control}
                            controlType='input'
                            label="Remarks"
                            idKey=""
                        />
                    </div>
                </form>
            </Modal>
        </div >
    )
}

export default EditReferralBusiness;
